import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CalendarClock, CheckCircle2, Clock, Search, X } from "lucide-react";
import { useMembers, useMembershipCategories, useMembershipStats } from "../hooks";
import { MEMBERSHIP_PERMISSIONS } from "../permissions";
import type { MemberCompany, MemberStatus } from "../types";
import {
  currentFYDates,
  fmtDate,
  fyStartYear,
  renewalInfo,
  toFinancialYear,
} from "../utils/financialYear";
import { MembershipAccess } from "../components/MembershipAccess";
import { MembershipPageHeader } from "../components/MembershipPageHeader";
import { MembershipPagination } from "../components/MembershipPagination";
import { MembershipStatusBadge } from "../components/MembershipStatusBadge";

type Tab = "members" | "renewals" | "pipeline";

const PAGE_SIZE = 20;

// ── Renewal badge ─────────────────────────────────────────────────────────────
function RenewalBadge({ member }: { member: MemberCompany }) {
  // Membership year = board approval year. renewalDueDate supplied by backend.
  // Fallback to client-side calculation from boardApprovalDate.
  const ri = renewalInfo(member.boardApprovalDate, member.renewalDueDate);
  const renewalDate = ri.nextRenewal;

  if (!renewalDate) return <span className="text-xs text-slate-400">—</span>;

  const daysUntil = Math.ceil(
    (renewalDate.getTime() - Date.now()) / 86_400_000
  );

  if (daysUntil < 0) {
    return (
      <div className="flex items-center gap-1.5">
        <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
        <span className="text-xs font-bold text-red-600">
          Overdue {Math.abs(daysUntil)}d
        </span>
      </div>
    );
  }
  if (daysUntil <= 30) {
    return (
      <div className="flex items-center gap-1.5">
        <Clock className="h-3.5 w-3.5 text-amber-500" />
        <span className="text-xs font-bold text-amber-600">
          {daysUntil}d left
        </span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5">
      <CalendarClock className="h-3.5 w-3.5 text-slate-400" />
      <span className="text-xs text-slate-500">{fmtDate(renewalDate)}</span>
    </div>
  );
}

// ── Pipeline stage groups ─────────────────────────────────────────────────────
const PIPELINE_STAGES: { label: string; statuses: MemberStatus[]; color: string }[] = [
  { label: "New / Prospect", statuses: ["PROSPECT", "PENDING_REVIEW"], color: "border-slate-300 bg-slate-50 text-slate-700" },
  { label: "Board Review", statuses: ["BOARD_PENDING", "BOARD_APPROVED", "BOARD_REJECTED"], color: "border-purple-200 bg-purple-50 text-purple-800" },
  { label: "Payment", statuses: ["PAYMENT_PENDING", "PAYMENT_CONFIRMED"], color: "border-orange-200 bg-orange-50 text-orange-800" },
  { label: "ICT Setup", statuses: ["ICT_PENDING", "ICT_COMPLETED"], color: "border-sky-200 bg-sky-50 text-sky-800" },
  { label: "Training", statuses: ["TRAINING_PENDING", "TRAINING_COMPLETED"], color: "border-teal-200 bg-teal-50 text-teal-800" },
  { label: "Active", statuses: ["ACTIVE_MEMBER"], color: "border-emerald-200 bg-emerald-50 text-emerald-800" },
  { label: "Suspended / Inactive", statuses: ["SUSPENDED", "INACTIVE", "REJECTED"], color: "border-red-200 bg-red-50 text-red-800" },
];

// ── Main page ─────────────────────────────────────────────────────────────────
export default function MembershipManagePage() {
  const [tab, setTab] = useState<Tab>("members");
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState<number | "">("");
  const [status, setStatus] = useState<MemberStatus | "">("");
  const { from: defaultFrom, to: defaultTo } = currentFYDates();
  const [fromDate, setFromDate] = useState(defaultFrom);
  const [toDate, setToDate] = useState(defaultTo);
  const selectedFY = fromDate ? toFinancialYear(new Date(fromDate)) : "";
  const statsQuery = useMembershipStats();
  const categoriesQuery = useMembershipCategories();

  const memberParams = useMemo(
    () => ({
      page,
      size: PAGE_SIZE,
      search,
      status,
      categoryId,
      membershipYear: selectedFY && tab === "renewals" ? fyStartYear(selectedFY) : "",
    }),
    [page, search, status, categoryId, selectedFY, tab]
  );

  const membersQuery = useMembers(memberParams);
  const data = membersQuery.data;
  const rows = data?.content ?? [];

  const reset = () => { setPage(0); };

  // Pipeline view — fetch without pagination limit for grouping
  const pipelineQuery = useMembers({ page: 0, size: 100 });
  const pipelineRows = pipelineQuery.data?.content ?? [];

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <MembershipPageHeader
        title="Membership Management"
        actions={
          <MembershipAccess permissions={[MEMBERSHIP_PERMISSIONS.CREATE, MEMBERSHIP_PERMISSIONS.MANAGE]}>
            <Link
              to="/hr/membership/create"
              className="inline-flex items-center gap-2 rounded-2xl bg-emerald-700 px-4 py-3 text-sm font-black text-white hover:bg-emerald-800"
            >
              + New Member
            </Link>
          </MembershipAccess>
        }
      />

      {/* ── Quick stats strip ───────────────────────────────────────── */}
      {statsQuery.data && (
        <div className="mt-4 grid gap-3 sm:grid-cols-3 xl:grid-cols-6">
          {[
            { label: "Total Members", value: statsQuery.data.totalMembers, color: "text-emerald-700" },
            { label: "Active", value: statsQuery.data.activeMembers, color: "text-emerald-700" },
            { label: "Board Pending", value: statsQuery.data.boardPending, color: "text-purple-700" },
            { label: "Payment Pending", value: statsQuery.data.paymentPending, color: "text-orange-700" },
            { label: "Overdue Payments", value: statsQuery.data.overduePayments, color: "text-red-700" },
            { label: "Pending Follow-ups", value: statsQuery.data.pendingTradeFollowUps, color: "text-indigo-700" },
          ].map(({ label, value, color }) => (
            <div key={label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-bold text-slate-500">{label}</p>
              <p className={`mt-1 text-2xl font-black ${color}`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* ── Tabs ───────────────────────────────────────────────────── */}
      <div className="mt-6 flex gap-2 border-b border-slate-200 pb-0">
        {(["members", "renewals", "pipeline"] as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => { setTab(t); reset(); }}
            className={`rounded-t-2xl px-5 py-3 text-sm font-black transition ${
              tab === t
                ? "border border-b-white border-slate-200 bg-white text-emerald-700"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {t === "members" ? "All Members" : t === "renewals" ? "Renewals" : "Pipeline"}
          </button>
        ))}
      </div>

      <div className="rounded-b-3xl rounded-tr-3xl border border-slate-200 bg-white shadow-sm">

        {/* ── MEMBERS TAB ─────────────────────────────────────────── */}
        {tab === "members" && (
          <>
            {/* Filter bar */}
            <div className="border-b border-slate-100 p-4">
              <div className="grid gap-3 md:grid-cols-4">
                <div className="relative md:col-span-2">
                  <Search className="absolute left-4 top-3.5 h-4 w-4 text-slate-400" />
                  <input
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); reset(); }}
                    placeholder="Search company name or code…"
                    className="w-full rounded-2xl border border-slate-200 py-3 pl-11 pr-4 text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                  />
                </div>
                <select value={categoryId} onChange={(e) => { setCategoryId(e.target.value === "" ? "" : Number(e.target.value)); reset(); }} className="rounded-2xl border border-slate-200 px-4 py-3 text-sm">
                  <option value="">All Categories</option>
                  {(categoriesQuery.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <select value={status} onChange={(e) => { setStatus(e.target.value as MemberStatus | ""); reset(); }} className="rounded-2xl border border-slate-200 px-4 py-3 text-sm">
                  <option value="">All Statuses</option>
                  <option value="PROSPECT">Prospect</option>
                  <option value="PENDING_REVIEW">Pending Review</option>
                  <option value="BOARD_PENDING">Board Pending</option>
                  <option value="BOARD_APPROVED">Board Approved</option>
                  <option value="PAYMENT_PENDING">Payment Pending</option>
                  <option value="PAYMENT_CONFIRMED">Payment Confirmed</option>
                  <option value="ICT_PENDING">ICT Pending</option>
                  <option value="TRAINING_PENDING">Training Pending</option>
                  <option value="ACTIVE_MEMBER">Active Member</option>
                  <option value="SUSPENDED">Suspended</option>
                </select>
              </div>
              {(search || status || categoryId !== "") && (
                <button type="button" onClick={() => { setSearch(""); setStatus(""); setCategoryId(""); reset(); }} className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-red-500 hover:underline">
                  <X className="h-3 w-3" /> Clear filters
                </button>
              )}
            </div>

            {/* Table */}
            <div className="px-5 py-3 flex items-center justify-between border-b border-slate-50">
              <p className="text-sm font-bold text-slate-500">
                {membersQuery.isLoading ? "Loading…" : `${data?.totalElements ?? 0} members`}
              </p>
              <p className="text-xs text-slate-400">Page {(data?.number ?? 0) + 1} of {data?.totalPages ?? 1}</p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs font-black uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Company</th>
                    <th className="px-4 py-3">Code</th>
                    <th className="px-4 py-3">Category</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Stage</th>
                    <th className="px-4 py-3">Active Since</th>
                    <th className="px-4 py-3">Next Renewal</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {membersQuery.isLoading ? (
                    <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">No members found.</td></tr>
                  ) : rows.map((m) => (
                    <tr key={m.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 font-black text-slate-900 whitespace-nowrap">{m.companyName}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-600">{m.membershipCode ?? "—"}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-slate-600">{m.categoryName}</td>
                      <td className="px-4 py-3"><MembershipStatusBadge value={m.status} /></td>
                      <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{m.workflowStage?.replace(/_/g, " ") ?? "—"}</td>
                      <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">{fmtDate(m.boardApprovalDate)}</td>
                      <td className="px-4 py-3"><RenewalBadge member={m} /></td>
                      <td className="px-4 py-3">
                        <Link to={`/hr/membership/members/${m.id}`} className="rounded-xl bg-emerald-700 px-3 py-1.5 text-xs font-black text-white hover:bg-emerald-800 whitespace-nowrap">
                          View / Edit
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {data && data.totalPages > 1 && (
              <div className="border-t border-slate-100 px-5 py-4">
                <MembershipPagination page={data} onPageChange={setPage} />
              </div>
            )}
          </>
        )}

        {/* ── RENEWALS TAB ─────────────────────────────────────────── */}
        {tab === "renewals" && (
          <>
            <div className="border-b border-slate-100 p-4">
              <div className="flex flex-wrap items-center gap-3">
                <div>
                  <label className="block text-xs font-black uppercase tracking-wide text-slate-400 mb-1">From</label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => { setFromDate(e.target.value); reset(); }}
                    className="rounded-2xl border border-slate-200 px-4 py-2.5 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-wide text-slate-400 mb-1">To</label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => { setToDate(e.target.value); reset(); }}
                    className="rounded-2xl border border-slate-200 px-4 py-2.5 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-wide text-slate-400 mb-1">Category</label>
                  <select value={categoryId} onChange={(e) => { setCategoryId(e.target.value === "" ? "" : Number(e.target.value)); reset(); }} className="rounded-2xl border border-slate-200 px-4 py-2.5 text-sm">
                    <option value="">All Categories</option>
                    {(categoriesQuery.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                {selectedFY && (
                  <p className="ml-auto text-xs text-slate-400 self-end pb-1">
                    Financial year: <strong>{selectedFY}</strong>
                  </p>
                )}
              </div>
            </div>

            {/* Renewal summary cards */}
            <RenewalSummary members={rows} />

            {/* Renewal table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs font-black uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Company</th>
                    <th className="px-4 py-3">Category</th>
                    <th className="px-4 py-3">Membership Code</th>
                    <th className="px-4 py-3">Active Since</th>
                    <th className="px-4 py-3">Join Year</th>
                    <th className="px-4 py-3">Next Renewal</th>
                    <th className="px-4 py-3">Renewal Status</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {membersQuery.isLoading ? (
                    <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">No members for this year.</td></tr>
                  ) : rows.map((m) => {
                    const ri = renewalInfo(m.boardApprovalDate, m.renewalDueDate);
                    return (
                      <tr key={m.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-black text-slate-900 whitespace-nowrap">{m.companyName}</td>
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{m.categoryName}</td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-600">{m.membershipCode ?? "—"}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-600">{fmtDate(m.boardApprovalDate)}</td>
                        <td className="px-4 py-3 text-xs text-slate-600">{ri.membershipYear ?? "—"}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-600">{fmtDate(m.renewalDueDate ?? ri.nextRenewal)}</td>
                        <td className="px-4 py-3"><RenewalStatusBadge member={m} /></td>
                        <td className="px-4 py-3">
                          <Link to={`/hr/membership/members/${m.id}`} className="rounded-xl bg-emerald-700 px-3 py-1.5 text-xs font-black text-white hover:bg-emerald-800">
                            View
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {data && data.totalPages > 1 && (
              <div className="border-t border-slate-100 px-5 py-4">
                <MembershipPagination page={data} onPageChange={setPage} />
              </div>
            )}
          </>
        )}

        {/* ── PIPELINE TAB ─────────────────────────────────────────── */}
        {tab === "pipeline" && (
          <div className="p-5">
            {pipelineQuery.isLoading ? (
              <p className="text-sm text-slate-400">Loading pipeline…</p>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {PIPELINE_STAGES.map(({ label, statuses, color }) => {
                  const group = pipelineRows.filter((m) => statuses.includes(m.status));
                  return (
                    <div key={label} className={`rounded-2xl border p-4 ${color}`}>
                      <div className="flex items-center justify-between mb-3">
                        <p className="font-black text-sm">{label}</p>
                        <span className="rounded-full bg-white/70 px-2.5 py-0.5 text-xs font-black">
                          {group.length}
                        </span>
                      </div>
                      {group.length === 0 ? (
                        <p className="text-xs opacity-60">None at this stage</p>
                      ) : (
                        <div className="space-y-1.5">
                          {group.slice(0, 5).map((m) => (
                            <Link
                              key={m.id}
                              to={`/hr/membership/members/${m.id}`}
                              className="flex items-center justify-between rounded-xl bg-white/60 px-3 py-2 hover:bg-white transition"
                            >
                              <div className="min-w-0">
                                <p className="text-xs font-black truncate">{m.companyName}</p>
                                <p className="text-xs opacity-70 truncate">{m.categoryName}</p>
                              </div>
                              <MembershipStatusBadge value={m.status} />
                            </Link>
                          ))}
                          {group.length > 5 && (
                            <p className="text-xs opacity-60 px-1">+{group.length - 5} more</p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

// ── Renewal summary cards ─────────────────────────────────────────────────────
function RenewalSummary({ members }: { members: MemberCompany[] }) {
  let overdue = 0, dueSoon = 0, upcoming = 0;
  for (const m of members) {
    const ri = renewalInfo(m.boardApprovalDate, m.renewalDueDate);
    if (ri.daysUntil === null) continue;
    if (ri.daysUntil < 0) overdue++;
    else if (ri.daysUntil <= 30) dueSoon++;
    else upcoming++;
  }
  return (
    <div className="grid gap-3 sm:grid-cols-3 border-b border-slate-100 p-4">
      <div className="rounded-2xl border border-red-200 bg-red-50 p-3 text-center">
        <p className="text-xs font-black text-red-600 uppercase">Overdue</p>
        <p className="text-3xl font-black text-red-700 mt-1">{overdue}</p>
      </div>
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-center">
        <p className="text-xs font-black text-amber-600 uppercase">Due ≤ 30 Days</p>
        <p className="text-3xl font-black text-amber-700 mt-1">{dueSoon}</p>
      </div>
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-3 text-center">
        <p className="text-xs font-black text-emerald-600 uppercase">Upcoming</p>
        <p className="text-3xl font-black text-emerald-700 mt-1">{upcoming}</p>
      </div>
    </div>
  );
}

function RenewalStatusBadge({ member }: { member: MemberCompany }) {
  const ri = renewalInfo(member.boardApprovalDate, member.renewalDueDate);
  if (member.status === "ACTIVE_MEMBER" && ri.daysUntil !== null) {
    if (ri.daysUntil < 0) return <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-black text-red-700">Overdue</span>;
    if (ri.daysUntil <= 30) return <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-black text-amber-700">Due Soon</span>;
    return (
      <div className="flex items-center gap-1">
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
        <span className="text-xs font-bold text-emerald-700">Current</span>
      </div>
    );
  }
  return <MembershipStatusBadge value={member.status} />;
}
